import {
  ClassificationLevel,
  type IntelTechnologyListItemDto,
  MopAggregation,
  ScoringRole,
  type StudyMoeEntriesSelectDto,
  type StudyMopSelectDto,
  StudyTaskEffort,
  type StudyTaskSelectDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal, ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { ModalSteps, useSteps } from '@components/shared/modal-steps.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover';
import { useApi } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { queryKeys } from '@queries/keys';
import {
  useCreateMeiaTask,
  useCreateMeiaTaskTechnology,
  useDeleteMeiaTask,
  useGetMeiaMopEntries,
  useGetMeiaSwimlaneMosEntries,
  useGetMeiaSwimlaneTasks,
  useGetMeiaTaskMoes,
  useGetMeiaTaskMops,
  useGetMeiaTaskMoses,
  useGetMeiaThreatTargetSystems,
  useUpdateMeiaTask,
} from '@queries/meia.query';
import { type MeiaTaskFormValues, meiaTaskSchema } from '@schemas/meia.schema';
import { useQueries } from '@tanstack/react-query';
import { createFormInvalidHandler } from '@utils/form.util';
import { LibraryIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { type FC, type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { MeiaMopCreateModal } from './meia-mop-create-modal.component';
import { MeiaTaskEngagementsForm } from './meia-task-engagements-form.component';
import {
  buildMeiaTaskFormDefaults,
  buildMeiaTaskModalResetKey,
  getNextMeiaTaskPosition,
  getThreatTargetSystemLibraryOptions,
} from './meia-task-modal.util';
import { MeiaTaskTechnologiesStep } from './meia-task-technologies-step.component';

type MeiaTaskModalProps = {
  isOpen: boolean;
  missionId: string;
  onClose: () => void;
  readOnly?: boolean;
  studyId: string;
  swimlaneId: string;
  task?: StudyTaskSelectDto;
};

const MOP_AGGREGATION_OPTIONS = [
  { label: 'Average', value: MopAggregation.AVERAGE },
  { label: 'Best', value: MopAggregation.BEST },
  { label: 'Worst', value: MopAggregation.WORST },
];

const SCORING_ROLE_OPTIONS = [
  { label: 'Contributing', value: ScoringRole.CONTRIBUTING },
  { label: 'Blocking', value: ScoringRole.BLOCKING },
];

const TASK_CLASSIFICATION_OPTIONS = [
  { label: 'U', value: ClassificationLevel.UNCLASSIFIED },
  { label: 'CUI', value: ClassificationLevel.CUI },
  { label: 'S', value: ClassificationLevel.SECRET },
  { label: 'TS', value: ClassificationLevel.TOP_SECRET },
];

const TASK_EFFORT_OPTIONS = [
  { label: 'S', value: StudyTaskEffort.S },
  { label: 'M', value: StudyTaskEffort.M },
  { label: 'L', value: StudyTaskEffort.L },
  { label: 'XL', value: StudyTaskEffort.XL },
];

const TASK_FIELD_TOOLTIPS = {
  moeIds: 'MoE describe observable outcomes that roll up to the selected MoS.',
  mopAggregation: 'Controls how selected MoP combine when this Task is scored.',
  mopIds: 'MoP are Mission-scoped metrics this Task will observe or require.',
  mosIds: 'MoS define the Stage outcomes this Task supports. A Task can link to several MoS.',
  scoringRole: 'Controls whether this Task contributes to scoring or blocks success when it fails.',
};

enum MeiaTaskModalStep {
  DETAILS = 0,
  TECHNOLOGIES = 1,
  RED_TARGET_ENGAGEMENT = 2,
}

const stepData = [
  { label: 'Details', step: MeiaTaskModalStep.DETAILS },
  { label: 'Technologies', step: MeiaTaskModalStep.TECHNOLOGIES },
  { label: 'Red Target Engagement', step: MeiaTaskModalStep.RED_TARGET_ENGAGEMENT },
];

function getPrimaryButtonLabel(params: {
  isEdit: boolean;
  isSaving: boolean;
  readOnly: boolean;
  step: MeiaTaskModalStep;
}): string {
  const { isEdit, isSaving, readOnly, step } = params;
  if (readOnly) return 'Done';
  if (step === MeiaTaskModalStep.DETAILS) {
    if (!isEdit) return 'Continue';
    return isSaving ? 'Saving...' : 'Save';
  }
  if (step === MeiaTaskModalStep.RED_TARGET_ENGAGEMENT) return 'Done';
  if (isEdit) return 'Done';
  return isSaving ? 'Saving...' : 'Save';
}

export const MeiaTaskModal: FC<MeiaTaskModalProps> = (props) => {
  const { isOpen, missionId, onClose, readOnly = false, studyId, swimlaneId, task } = props;
  const [draftTechnologies, setDraftTechnologies] = useState<IntelTechnologyListItemDto[]>([]);
  const effectiveTask = task;
  const hasPersistedTask = Boolean(effectiveTask);
  const isEdit = Boolean(task);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // selected MoS is derived from form state via useStore inside JSX
  const [isCreatingMop, setIsCreatingMop] = useState(false);
  const [isThreatTargetLibraryOpen, setIsThreatTargetLibraryOpen] = useState(false);
  const resetKeyRef = useRef<string | null>(null);
  const autoFilledForKeyRef = useRef<string | null>(null);
  const { handleStepClick, resetSteps, step, stepsInfo, updateStepState } = useSteps(
    stepData,
    MeiaTaskModalStep.DETAILS,
    isEdit,
  );

  const { data: mosEntries = [] } = useGetMeiaSwimlaneMosEntries({ swimlaneId });
  const { data: mopEntries = [] } = useGetMeiaMopEntries({ missionId });
  const { data: swimlaneTasks = [] } = useGetMeiaSwimlaneTasks({ studyId, swimlaneId });
  const { data: existingTaskMoes = [] } = useGetMeiaTaskMoes({ studyId, taskId: effectiveTask?.id });
  const { data: existingTaskMops = [] } = useGetMeiaTaskMops({ studyId, taskId: effectiveTask?.id });
  const { data: existingTaskMoses = [] } = useGetMeiaTaskMoses({ studyId, taskId: effectiveTask?.id });
  const { data: threatTargetSystems = [], isLoading: isLoadingThreatTargetSystems } = useGetMeiaThreatTargetSystems();
  const threatTargetLibraryOptions = useMemo(
    () => getThreatTargetSystemLibraryOptions(threatTargetSystems),
    [threatTargetSystems],
  );

  const { mutateAsync: createTask } = useCreateMeiaTask();
  const { mutateAsync: createDraftTaskTechnology } = useCreateMeiaTaskTechnology();
  const { mutateAsync: updateTask } = useUpdateMeiaTask();
  const { mutateAsync: deleteTask } = useDeleteMeiaTask();

  const form = useAppForm({
    defaultValues: buildMeiaTaskFormDefaults({
      task: effectiveTask,
      taskMoes: existingTaskMoes,
      taskMops: existingTaskMops,
      taskMoses: existingTaskMoses,
    }),
    onSubmit: async ({ value }) => {
      if (readOnly) {
        handleClose();
        return;
      }

      const validation = meiaTaskSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please complete every required field.' });
        return;
      }
      const payload = validation.data;

      setIsSaving(true);
      try {
        if (effectiveTask) {
          await updateTask({
            classification: payload.classification,
            description: payload.description ?? '',
            effort: payload.effort,
            expectedThreatTargetSystemCode: payload.expectedThreatTargetSystemCode || null,
            moeIds: payload.moeIds,
            mopAggregation: payload.mopAggregation,
            mopIds: payload.mopIds,
            mosIds: payload.mosIds,
            owner: payload.owner || null,
            scoringRole: payload.scoringRole,
            studyId,
            taskId: effectiveTask.id,
            taskName: payload.taskName,
          });
          successToast({ description: 'Task updated.' });
        } else {
          const nextTask = await createTask({
            classification: payload.classification,
            description: payload.description ?? '',
            effort: payload.effort,
            expectedThreatTargetSystemCode: payload.expectedThreatTargetSystemCode || null,
            moeIds: payload.moeIds,
            mopAggregation: payload.mopAggregation,
            mopIds: payload.mopIds,
            mosIds: payload.mosIds,
            owner: payload.owner || null,
            position: getNextMeiaTaskPosition(swimlaneTasks.map((swimlaneTask) => swimlaneTask.position)),
            scoringRole: payload.scoringRole,
            studyId,
            swimlaneId,
            taskName: payload.taskName,
          });
          await Promise.all(
            draftTechnologies.map((technology) =>
              createDraftTaskTechnology({
                function: technology.name,
                studyId,
                taskId: nextTask.id,
                technologyId: technology.id,
              }),
            ),
          );
          successToast({ description: 'Task created.' });
        }

        handleClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save task.';
        errorToast({ description: message });
      } finally {
        setIsSaving(false);
      }
    },
    onSubmitInvalid: createFormInvalidHandler<MeiaTaskFormValues>(),
    validators: { onSubmit: meiaTaskSchema },
  });

  useEffect(() => {
    if (!isOpen) {
      resetKeyRef.current = null;
      autoFilledForKeyRef.current = null;
      return;
    }

    const resetKey = buildMeiaTaskModalResetKey({
      existingTaskMoeIds: existingTaskMoes.map((entry) => entry.studyMoeId),
      existingTaskMopIds: existingTaskMops.map((entry) => entry.studyMopId),
      existingTaskMosIds: existingTaskMoses.map((entry) => entry.studyMosId),
      isOpen,
      swimlaneId,
      taskId: effectiveTask?.id ?? null,
      taskSnapshotKey: effectiveTask
        ? JSON.stringify({
            classification: effectiveTask.classification,
            description: effectiveTask.description,
            effort: effectiveTask.effort,
            expectedThreatTargetSystemCode: effectiveTask.expectedThreatTargetSystemCode,
            mopAggregation: effectiveTask.mopAggregation,
            owner: effectiveTask.owner,
            scoringRole: effectiveTask.scoringRole,
            taskName: effectiveTask.taskName,
          })
        : undefined,
    });
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    form.reset(
      buildMeiaTaskFormDefaults({
        task: effectiveTask,
        taskMoes: existingTaskMoes,
        taskMops: existingTaskMops,
        taskMoses: existingTaskMoses,
      }),
    );
  }, [existingTaskMoes, existingTaskMops, existingTaskMoses, form, isOpen, swimlaneId, effectiveTask]);

  const handleConfirmDelete = async (): Promise<void> => {
    if (readOnly) return;
    if (!effectiveTask) return;
    await deleteTask({ studyId, swimlaneId: effectiveTask.swimlaneId, taskId: effectiveTask.id });
    successToast({ description: 'Task deleted.' });
    handleClose();
  };

  const handleMopCreated = (mop: StudyMopSelectDto): void => {
    if (readOnly) return;
    form.setFieldValue('mopIds', (current) => [...new Set([...(current ?? []), mop.id])]);
  };

  const handleClose = (): void => {
    setDraftTechnologies([]);
    resetSteps();
    resetKeyRef.current = null;
    autoFilledForKeyRef.current = null;
    form.reset(
      buildMeiaTaskFormDefaults({
        task,
        taskMoes: existingTaskMoes,
        taskMops: existingTaskMops,
        taskMoses: existingTaskMoses,
      }),
    );
    onClose();
  };

  const handleStepClickWithValidation = (targetStep: number): void => {
    if (targetStep === step) return;
    if (targetStep === MeiaTaskModalStep.RED_TARGET_ENGAGEMENT && !hasPersistedTask) {
      errorToast({ description: 'Save the task before binding red target engagements.' });
      return;
    }
    if (targetStep === MeiaTaskModalStep.TECHNOLOGIES && !hasPersistedTask) {
      const validation = meiaTaskSchema.safeParse(form.state.values);
      if (!validation.success) {
        void form.validateAllFields('submit');
        errorToast({ description: 'Please complete every required field.' });
        return;
      }
      updateStepState(MeiaTaskModalStep.DETAILS);
      return;
    }
    handleStepClick(targetStep);
  };

  const handlePrimaryAction = (): void => {
    if (readOnly) {
      handleClose();
      return;
    }

    if (step === MeiaTaskModalStep.DETAILS) {
      if (!isEdit) {
        handleStepClickWithValidation(MeiaTaskModalStep.TECHNOLOGIES);
        return;
      }
      void form.handleSubmit();
      return;
    }
    if (step === MeiaTaskModalStep.RED_TARGET_ENGAGEMENT) {
      handleClose();
      return;
    }
    if (!hasPersistedTask) {
      void form.handleSubmit();
      return;
    }
    handleClose();
  };

  return (
    <MeiaTaskEngagementsForm
      onSaved={handleClose}
      readOnly={readOnly}
      studyId={studyId}
      task={effectiveTask}
    >
      {(engagementsApi) => {
        // Guard every close path (X, overlay, Escape) while the engagements step holds unsaved edits.
        const requestClose = (): void => {
          if (!readOnly && engagementsApi.getIsDirty()) {
            setIsConfirmingDiscard(true);
            return;
          }
          engagementsApi.reset();
          handleClose();
        };

        return (
          <>
            <Modal
              centered
              footer={{
                leftButton:
                  hasPersistedTask && !readOnly ? (
                    <Button
                      onClick={() => setIsConfirmingDelete(true)}
                      type="button"
                      variant="destructive"
                    >
                      <Trash2Icon className="size-4" />
                      Delete
                    </Button>
                  ) : undefined,
                rightButton:
                  step === MeiaTaskModalStep.RED_TARGET_ENGAGEMENT && !readOnly
                    ? {
                        children: engagementsApi.isSaving ? 'Saving...' : 'Save',
                        disabled: engagementsApi.isSaving || engagementsApi.isLoading,
                        onClick: engagementsApi.submit,
                      }
                    : {
                        children: getPrimaryButtonLabel({ isEdit, isSaving, readOnly, step }),
                        disabled: isSaving,
                        onClick: handlePrimaryAction,
                      },
              }}
              header={{ title: readOnly && isEdit ? 'Task Details' : isEdit ? 'Edit Task' : 'Add Task' }}
              isOpen={isOpen}
              onClose={requestClose}
              width="720px"
            >
              <div className="mb-4">
                <ModalSteps
                  onStepClick={handleStepClickWithValidation}
                  stepsInfo={stepsInfo}
                />
              </div>
              <form
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                className={cn(step === MeiaTaskModalStep.DETAILS ? 'flex flex-col gap-4' : 'hidden')}
                onSubmit={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void form.handleSubmit();
                }}
              >
                <form.AppForm>
                  <form.AppField name="taskName">
                    {(field) => (
                      <field.TextField
                        {...MEIA_AUTOFILL_IGNORE_PROPS}
                        disabled={readOnly}
                        label="Name"
                        placeholder="e.g., Cue Growler SIGINT on forward SAM-band emissions"
                        required
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="description">
                    {(field) => (
                      <field.TextAreaField
                        label="Description"
                        placeholder="What does this Task accomplish? Who owns it? What inputs does it consume?"
                        rows={3}
                        disabled={readOnly}
                      />
                    )}
                  </form.AppField>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <form.AppField name="owner">
                      {(field) => (
                        <field.TextField
                          {...MEIA_AUTOFILL_IGNORE_PROPS}
                          disabled={readOnly}
                          label="Owner"
                          placeholder="e.g., Capt. Vance"
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="effort">
                      {(field) => (
                        <field.SelectField
                          label="Effort"
                          options={TASK_EFFORT_OPTIONS}
                          placeholder="Select effort"
                          disabled={readOnly}
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="classification">
                      {(field) => (
                        <field.SelectField
                          label="Classification"
                          options={TASK_CLASSIFICATION_OPTIONS}
                          placeholder="Select classification"
                          disabled={readOnly}
                        />
                      )}
                    </form.AppField>
                  </div>

                  <form.AppField name="mosIds">
                    {(field) => (
                      <field.SelectMultipleField
                        label="Measures of Success"
                        noResultsMessage="No Measures of Success yet. Add MoS to this Mission Thread first."
                        options={mosEntries.map((mos) => ({
                          label: mos.description,
                          value: mos.id,
                        }))}
                        placeholder="Select a MoS"
                        tooltip={TASK_FIELD_TOOLTIPS.mosIds}
                        disabled={readOnly}
                      />
                    )}
                  </form.AppField>

                  <form.Subscribe selector={(state) => state.values.mosIds}>
                    {(currentMosIds) => (
                      <MeiaTaskMoeFieldSection
                        autoFilledForKeyRef={autoFilledForKeyRef}
                        isCreateFlow={!isEdit}
                        isOpen={isOpen}
                        mosIds={currentMosIds}
                        setMoeIds={(next) => form.setFieldValue('moeIds', next)}
                      >
                        {({ isDisabled, moeOptions, placeholder }) => (
                          <form.AppField name="moeIds">
                            {(field) => (
                              <field.SelectMultipleField
                                disabled={isDisabled || readOnly}
                                label="Measures of Effectiveness"
                                options={moeOptions}
                                placeholder={placeholder}
                                tooltip={TASK_FIELD_TOOLTIPS.moeIds}
                              />
                            )}
                          </form.AppField>
                        )}
                      </MeiaTaskMoeFieldSection>
                    )}
                  </form.Subscribe>

                  <div className="flex flex-col gap-2">
                    <form.AppField name="mopIds">
                      {(field) => (
                        <field.SelectMultipleField
                          label="Measures of Performance"
                          options={mopEntries.map((mop) => ({ label: mop.name, value: mop.id }))}
                          placeholder={
                            mopEntries.length === 0 ? 'Create MoP or select it after it exists' : 'Select MoP'
                          }
                          tooltip={TASK_FIELD_TOOLTIPS.mopIds}
                          disabled={readOnly}
                        />
                      )}
                    </form.AppField>
                    {!readOnly && (
                      <Button
                        className="w-fit"
                        onClick={() => setIsCreatingMop(true)}
                        size="sm"
                        type="button"
                        variant="link"
                      >
                        <PlusIcon className="size-3.5" />
                        New MoP
                      </Button>
                    )}
                  </div>

                  <form.AppField name="expectedThreatTargetSystemCode">
                    {(field) => (
                      <Popover
                        onOpenChange={setIsThreatTargetLibraryOpen}
                        open={isThreatTargetLibraryOpen}
                      >
                        <div className="relative">
                          <field.TextField
                            {...MEIA_AUTOFILL_IGNORE_PROPS}
                            disabled={readOnly}
                            inputClassName="pr-10"
                            label="Expected Threat Target System"
                            placeholder="e.g., TTS-IADS-001 or forward radar site Alpha"
                          />
                          {!readOnly && (
                            <PopoverAnchor asChild>
                              <IconButton
                                ariaLabel="Open Threat Target System library"
                                className="absolute top-7 right-2"
                                icon={LibraryIcon}
                                onClick={() => setIsThreatTargetLibraryOpen(true)}
                                size="sm"
                                type="button"
                                variant="text"
                              />
                            </PopoverAnchor>
                          )}
                        </div>
                        <PopoverContent
                          align="end"
                          className="w-(--radix-popover-trigger-width) min-w-80 max-w-[480px] p-2"
                        >
                          <div className="max-h-72 overflow-y-auto rounded-sm border border-border">
                            {isLoadingThreatTargetSystems ? (
                              <div className="p-4 text-sm text-muted-foreground">Loading library...</div>
                            ) : threatTargetLibraryOptions.length === 0 ? (
                              <div className="p-4 text-sm text-muted-foreground">No systems in library.</div>
                            ) : (
                              threatTargetLibraryOptions.map((option) => (
                                <button
                                  className="flex w-full cursor-pointer flex-col items-start gap-1 border-b border-border p-3 text-left transition-colors last:border-b-0 hover:bg-gray-50"
                                  disabled={readOnly}
                                  key={option.value}
                                  onClick={() => {
                                    form.setFieldValue('expectedThreatTargetSystemCode', option.value);
                                    setIsThreatTargetLibraryOpen(false);
                                  }}
                                  type="button"
                                >
                                  <span className="font-medium text-gray-900">{option.label}</span>
                                  <span className="text-xs text-muted-foreground">{option.description}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </form.AppField>

                  <Accordion
                    collapsible
                    type="single"
                  >
                    <AccordionItem
                      className="border-b-0"
                      value="advanced"
                    >
                      <AccordionTrigger className="ml-auto w-fit !flex-none gap-2 py-1 text-xs font-medium text-gray-600">
                        Advanced Scoring
                      </AccordionTrigger>
                      <AccordionContent className="pt-2">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <form.AppField name="scoringRole">
                            {(field) => (
                              <field.SelectField
                                label="Scoring Role"
                                options={SCORING_ROLE_OPTIONS}
                                required
                                tooltip={TASK_FIELD_TOOLTIPS.scoringRole}
                                disabled={readOnly}
                              />
                            )}
                          </form.AppField>
                          <form.AppField name="mopAggregation">
                            {(field) => (
                              <field.SelectField
                                label="MoP Aggregation"
                                options={MOP_AGGREGATION_OPTIONS}
                                required
                                tooltip={TASK_FIELD_TOOLTIPS.mopAggregation}
                                disabled={readOnly}
                              />
                            )}
                          </form.AppField>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </form.AppForm>
              </form>

              {step === MeiaTaskModalStep.TECHNOLOGIES && (
                <MeiaTaskTechnologiesStep
                  draftTechnologies={draftTechnologies}
                  readOnly={readOnly}
                  setDraftTechnologies={setDraftTechnologies}
                  studyId={studyId}
                  task={effectiveTask}
                />
              )}

              {step === MeiaTaskModalStep.RED_TARGET_ENGAGEMENT && engagementsApi.body}
            </Modal>

            {effectiveTask && !readOnly && (
              <ConfirmDeleteModal
                isOpen={isConfirmingDelete}
                itemName="task"
                onConfirm={handleConfirmDelete}
                setIsOpen={setIsConfirmingDelete}
              />
            )}
            {isCreatingMop && !readOnly && (
              <MeiaMopCreateModal
                isOpen={isCreatingMop}
                missionId={missionId}
                onClose={() => setIsCreatingMop(false)}
                onCreated={handleMopCreated}
              />
            )}
            <ConfirmationModal
              cancelLabel="Keep Editing"
              confirmLabel="Discard Changes"
              description="Closing now will discard the unsaved red target engagement edits."
              isOpen={isConfirmingDiscard}
              onConfirm={() => {
                engagementsApi.reset();
                handleClose();
              }}
              setIsOpen={setIsConfirmingDiscard}
              title="Discard unsaved changes?"
            />
          </>
        );
      }}
    </MeiaTaskEngagementsForm>
  );
};

type MeiaTaskMoeFieldSectionProps = {
  autoFilledForKeyRef: MutableRefObject<string | null>;
  children: (params: {
    isDisabled: boolean;
    moeOptions: { label: string; value: string }[];
    placeholder: string;
  }) => React.ReactNode;
  /** Auto-fill MoE only fires for create flow; on edit we never overwrite saved selections. */
  isCreateFlow: boolean;
  isOpen: boolean;
  mosIds: string[];
  setMoeIds: (next: string[]) => void;
};

const MeiaTaskMoeFieldSection: FC<MeiaTaskMoeFieldSectionProps> = (props) => {
  const { autoFilledForKeyRef, children, isCreateFlow, isOpen, mosIds, setMoeIds } = props;
  const { apiGet } = useApi();

  const moeQueries = useQueries({
    queries: mosIds.map((mosId) => ({
      enabled: Boolean(mosId),
      queryFn: async () => {
        const data = await apiGet<StudyMoeEntriesSelectDto>({
          path: `/v1/intel/mos-entries/${mosId}/moe-entries`,
        });
        return data.moeEntries;
      },
      queryKey: queryKeys.meia.getMoeEntries(mosId),
    })),
  });

  // Stable signature so downstream memos don't churn on every parent render — useQueries returns
  // a fresh array reference each render, so memoizing on it directly is a no-op.
  const moeQueriesSignature = moeQueries.map((query) => `${query.status}:${query.data?.length ?? 0}`).join('|');
  const moeEntries = useMemo(() => {
    void moeQueriesSignature;
    return moeQueries.flatMap((query) => query.data ?? []);
  }, [moeQueries, moeQueriesSignature]);
  const moeOptions = useMemo(() => moeEntries.map((moe) => ({ label: moe.description, value: moe.id })), [moeEntries]);
  const allLoaded = moeQueries.every((query) => !query.isLoading);
  const placeholder =
    mosIds.length === 0 ? 'Select MoS first' : moeEntries.length === 0 ? 'No MoE under selected MoS yet' : 'Select MoE';

  useEffect(() => {
    if (!isCreateFlow) return;
    if (!isOpen || mosIds.length === 0) return;
    if (!allLoaded) return;
    const key = [...mosIds].sort().join(',');
    if (autoFilledForKeyRef.current === key) return;
    autoFilledForKeyRef.current = key;
    setMoeIds(moeEntries.map((moe) => moe.id));
  }, [allLoaded, autoFilledForKeyRef, isCreateFlow, isOpen, moeEntries, mosIds, setMoeIds]);

  return <>{children({ isDisabled: mosIds.length === 0, moeOptions, placeholder })}</>;
};
